import { useEffect, useState } from "react";
import { Menu, X, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { href: "#workouts", label: "Workouts", testid: "nav-workouts-link" },
  { href: "#progress", label: "Progress", testid: "nav-progress-link" },
  { href: "#trainers", label: "Trainers", testid: "nav-trainers-link" },
  { href: "#pricing", label: "Pricing", testid: "nav-pricing-link" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="nav-header"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[rgba(11,15,20,0.78)] backdrop-blur-md border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo-link" className="flex items-center gap-2 group">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-neon-lime text-ink-0 shadow-glow-lime">
            <Zap className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <span className="font-display text-2xl tracking-wider text-white">PULSE</span>
          <span className="hidden sm:inline-flex items-center gap-1 ml-2 text-[10px] uppercase tracking-widest text-neon-lime">
            <span className="h-1.5 w-1.5 rounded-full bg-neon-lime pulse-dot" />
            live
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="link-underline text-sm text-white/80 hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href="#pricing" className="hidden sm:block">
            <Button
              data-testid="nav-primary-cta-button"
              className="btn-lift h-10 px-5 rounded-xl bg-neon-lime text-ink-0 hover:bg-neon-lime hover:brightness-95 font-semibold"
            >
              Start Free Trial
            </Button>
          </a>
          <button
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white btn-lift"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden overflow-hidden border-b border-white/10 bg-ink-0 transition-[max-height,opacity] duration-300 ${
          open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`${l.testid}-mobile`}
              className="text-white/85 text-base py-2 border-b border-white/5"
            >
              {l.label}
            </a>
          ))}
          <a href="#pricing" onClick={() => setOpen(false)}>
            <Button
              data-testid="nav-primary-cta-button-mobile"
              className="w-full mt-2 h-11 rounded-xl bg-neon-lime text-ink-0 font-semibold hover:brightness-95"
            >
              Start Free Trial
            </Button>
          </a>
        </div>
      </div>
    </header>
  );
};
